/**
 * Cost of Sales.
 *
 * Winnie's v8 note: capture every acquisition and direct cost against the vehicle it
 * belongs to, recorded to the VIN. Her own screen groups those costs four ways, and
 * this service reads and writes that shape.
 *
 * Everything lives in the one expenses table. expense_type says which grouping a row
 * belongs to; nothing is duplicated, so a cost entered here is the same row the P&L,
 * the month-end stocktake and the payables list already read.
 */

import { supabase } from '../lib/supabase'
import { DateRange, ALL_TIME, inRange } from '../lib/dateRange'
import { categoryOf } from '../lib/costCategories'
import { CostType } from '../lib/commission'
import { formatAbn, isValidAbn } from '../lib/abn'

export type ExpenseType = 'general' | 'warranty' | 'government_fee' | 'incentive_bonus'
export type CostStatus = 'billed' | 'paid' | 'void'

export const EXPENSE_TYPES: { value: ExpenseType; label: string; needsVehicle: boolean }[] = [
  { value: 'general', label: 'General', needsVehicle: true },
  { value: 'warranty', label: 'Warranty', needsVehicle: true },
  { value: 'government_fee', label: 'Government fee', needsVehicle: false },
  { value: 'incentive_bonus', label: 'Incentive bonus', needsVehicle: false },
]

/**
 * The seven the database allows, which are also the seven the P&L maps its Cost of
 * Sales accounts from. A category outside this list would drop out of the accounts
 * without saying so, which is why the column is still constrained.
 */
export const CATEGORIES: { value: string; label: string }[] = [
  { value: 'transport', label: 'Transport / freight' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'reconditioning', label: 'Reconditioning' },
  { value: 'ppsr', label: 'PPSR' },
  { value: 'buying_fee', label: 'Buying fee' },
  { value: 'finance_payout', label: 'Finance payout' },
  { value: 'other', label: 'Other' },
]

export const categoryLabel = (key: string) => {
  const hit = CATEGORIES.find((c) => c.value === key)
  return hit ? hit.label : key || ''
}

export const typeLabel = (key: string) => {
  const hit = EXPENSE_TYPES.find((t) => t.value === key)
  return hit ? hit.label : key || ''
}

export interface CostRow {
  id: string
  expenseType: ExpenseType
  dealId: string | null
  vehicle: string
  vin: string
  stockNumber: string
  date: string | null
  paidTo: string
  /** The supplier's ABN, digits only, or ''. Read off the invoice where there was one. */
  supplierAbn: string
  nonAccounting: boolean
  amount: number
  taxInvoice: boolean
  gst: number
  status: CostStatus
  datePaid: string | null
  bankAccount: string
  paymentMethod: string
  category: string
  reference: string
  description: string
  supplierInvoice: string
  poNumber: string
  bulkPaymentRef: string
  capitalised: boolean
  costType: CostType | null
  /** The uploaded invoice behind this cost, when it came from one. */
  supplierInvoiceId: string | null
  invoiceFilePath: string | null
  invoiceFileName: string
}

export interface CostInput {
  id?: string
  expenseType: ExpenseType
  dealId: string | null
  date: string
  paidTo: string
  supplierAbn: string
  amount: string
  gst: string
  taxInvoice: boolean
  nonAccounting: boolean
  category: string
  description: string
  supplierInvoice: string
  reference: string
  poNumber: string
  bankAccount: string
  paymentMethod: string
  bulkPaymentRef: string
  paid: boolean
  datePaid: string
  capitalised: boolean
  costType?: CostType | null
}

export const EMPTY_COST: CostInput = {
  expenseType: 'general',
  dealId: null,
  date: new Date().toISOString().slice(0, 10),
  paidTo: '',
  supplierAbn: '',
  amount: '',
  gst: '',
  taxInvoice: false,
  nonAccounting: false,
  category: 'transport',
  description: '',
  supplierInvoice: '',
  reference: '',
  poNumber: '',
  bankAccount: '',
  paymentMethod: '',
  bulkPaymentRef: '',
  paid: false,
  datePaid: '',
  capitalised: true,
  costType: null,
}

export interface VehicleOption {
  id: string
  label: string
  vin: string
  stockNumber: string
}

const money = (v: unknown) => Math.round((Number(v) || 0) * 100) / 100
const day = (v: unknown) => (v ? String(v).slice(0, 10) : null)
const str = (v: unknown) => (v == null ? '' : String(v))

/* Every vehicle on this platform carries its VIN in vehicle_vin. The AutoGrab columns are
   filled only for deals sourced through AutoGrab - 2 of 160 at the time of writing, and not
   one of them with a VIN - so reading autograb_vin alone is why the Cost of Sales vehicle
   picker had no VIN to offer and every car showed as "#n Vehicle". */
const vinOf = (deal: Record<string, unknown> | null): string =>
  deal ? str(deal.autograb_vin) || str(deal.vehicle_vin) : ''

function describe(deal: Record<string, unknown> | null): string {
  if (!deal) return ''
    const pick = (a: string, b: string) => {
      const first = deal[a] == null ? '' : String(deal[a]).trim()
      return first || (deal[b] == null ? '' : String(deal[b]).trim())
    }
    const parts = [
      pick('autograb_year', 'vehicle_year'),
      pick('autograb_make', 'vehicle_make'),
      pick('autograb_model', 'vehicle_model'),
    ].filter(Boolean)
  const stock = deal.stock_number == null ? '' : String(deal.stock_number)
  return (stock ? '#' + stock + ' ' : '') + (parts.join(' ') || 'Vehicle')
}

function statusOf(row: Record<string, unknown>): CostStatus {
  const s = String(row.payment_status || 'awaiting_payment')
  if (s === 'void') return 'void'
  if (s === 'paid') return 'paid'
  return 'billed'
}

export async function loadCostOfSales(range: DateRange = ALL_TIME): Promise<CostRow[]> {
  const { data, error } = await supabase
    .from('expenses')
    .select(
      'id, deal_id, expense_type, category, description, amount, gst_amount, supplier, paid_to, ' +
        'supplier_abn, supplier_invoice_id, ' +
        'invoice_ref, incurred_on, capitalised, non_accounting, tax_invoice, bank_account, cost_type, ' +
        'payment_method, reference, po_number, bulk_payment_ref, payment_status, paid_at, created_at, ' +
        'invoice:supplier_invoices!supplier_invoice_id(id, file_path, file_name), ' +
        'deal:deals!deal_id(id, stock_number, autograb_vin, autograb_year, autograb_make, ' +
          'autograb_model, vehicle_vin, vehicle_year, vehicle_make, vehicle_model)',
    )
    .order('incurred_on', { ascending: false })

  if (error) throw error

  return ((data || []) as unknown as Record<string, unknown>[])
    .map((row) => {
      const deal = (row.deal || null) as Record<string, unknown> | null
      const invoice = (row.invoice || null) as Record<string, unknown> | null
      return {
        id: String(row.id),
        expenseType: (String(row.expense_type || 'general') as ExpenseType),
        dealId: deal ? String(deal.id) : row.deal_id ? String(row.deal_id) : null,
        vehicle: describe(deal),
        vin: vinOf(deal),
        stockNumber: deal && deal.stock_number != null ? String(deal.stock_number) : '',
        date: day(row.incurred_on) || day(row.created_at),
        paidTo: str(row.paid_to) || str(row.supplier),
        supplierAbn: str(row.supplier_abn),
        nonAccounting: Boolean(row.non_accounting),
        amount: money(row.amount),
        taxInvoice: Boolean(row.tax_invoice),
        gst: money(row.gst_amount),
        status: statusOf(row),
        datePaid: day(row.paid_at),
        bankAccount: str(row.bank_account),
        paymentMethod: str(row.payment_method),
        category: str(row.category),
        reference: str(row.reference),
        description: str(row.description),
        supplierInvoice: str(row.invoice_ref),
        poNumber: str(row.po_number),
        bulkPaymentRef: str(row.bulk_payment_ref),
        capitalised: Boolean(row.capitalised),
        costType: (row.cost_type as CostType) || null,
        supplierInvoiceId: row.supplier_invoice_id ? String(row.supplier_invoice_id) : null,
        invoiceFilePath: invoice && invoice.file_path ? String(invoice.file_path) : null,
        invoiceFileName: invoice && invoice.file_name ? String(invoice.file_name) : '',
      }
    })
    .filter((r) => inRange(r.date, range))
}

export async function loadVehicles(): Promise<VehicleOption[]> {
  const { data, error } = await supabase
    .from('deals')
      .select(
        'id, stock_number, autograb_vin, autograb_year, autograb_make, autograb_model, ' +
          'vehicle_vin, vehicle_year, vehicle_make, vehicle_model',
      )
    .is('deleted_at', null)
    .order('created_at', { ascending: true })

  if (error) throw error

  return ((data || []) as unknown as Record<string, unknown>[]).map((d) => ({
    id: String(d.id),
      label: describe(d) + (vinOf(d) ? ' — ' + vinOf(d) : ''),
      vin: vinOf(d),
    stockNumber: d.stock_number == null ? '' : String(d.stock_number),
  }))
}

/** Everything a row needs, shared by insert and update so the two cannot drift apart. */
function toRecord(input: CostInput, userId: string | null) {
  const needsVehicle = input.expenseType === 'general' || input.expenseType === 'warranty'
  return {
    deal_id: needsVehicle ? input.dealId : input.dealId || null,
    expense_type: input.expenseType,
    category: input.category,
    /* The v2 cost type the accrual logic reads. Without it a hand-entered inspection bill is
       invisible to `accrualPlan`, which then accrues a standard inspection cost on top of it -
       the vehicle carries the cost twice and the margin, and the commission, are wrong.

       `transport` resolves to null on purpose: freight in and freight out sit on opposite
       sides of the margin and the category cannot say which. The split-invoice form asks; the
       single-vehicle form does not yet, so a hand-entered freight bill still needs its leg
       chosen. That is a known gap, not an oversight. */
    cost_type: input.costType ?? categoryOf(input.category)?.costType ?? null,
    description: input.description || null,
    amount: Number(input.amount) || 0,
    gst_amount: Number(input.gst) || 0,
    supplier: input.paidTo || null,
    paid_to: input.paidTo || null,
    supplier_abn: input.supplierAbn ? input.supplierAbn.replace(/\D/g, '') : null,
    invoice_ref: input.supplierInvoice || null,
    incurred_on: input.date || null,
    capitalised: input.capitalised,
    non_accounting: input.nonAccounting,
    tax_invoice: input.taxInvoice,
    bank_account: input.bankAccount || null,
    payment_method: input.paymentMethod || null,
    reference: input.reference || null,
    po_number: input.poNumber || null,
    bulk_payment_ref: input.bulkPaymentRef || null,
    payment_status: input.paid ? 'paid' : 'awaiting_payment',
    paid_at: input.paid ? new Date((input.datePaid || input.date) + 'T00:00:00Z').toISOString() : null,
    created_by: userId,
  }
}

/** What is wrong with this entry, in words, or an empty list. */
export function validateCost(input: CostInput): string[] {
  const problems: string[] = []
  const needsVehicle = input.expenseType === 'general' || input.expenseType === 'warranty'
  if (needsVehicle && !input.dealId) problems.push('Choose the vehicle this cost belongs to.')
  if (!input.paidTo.trim()) problems.push('Enter who it was paid to.')
  /* Winnie, 13 Sep: an ABN that fails the checksum is an OCR misread or a typo, and is
     not recorded - "don't record a bad ABN just because something was found on the page."
     Empty stays allowed: the ABN is a help, never a gate on entering a cost. */
  if (input.supplierAbn.trim() && !isValidAbn(input.supplierAbn.replace(/\D/g, ''))) {
    problems.push('That supplier ABN does not pass the checksum — check the digits, or leave it blank.')
  }
  if (!(Number(input.amount) > 0)) problems.push('Enter an amount greater than zero.')
  if (!input.description.trim()) problems.push('Enter a description.')
  if (!input.supplierInvoice.trim()) problems.push('Enter the supplier invoice number.')
  /* Winnie's own rule, written into costCategories: a category that cannot say which side of
     the sale a cost belongs to has to be asked about rather than guessed at. The split-invoice
     form already refuses to save without an answer; this is the same refusal on the
     single-vehicle form. Guessing puts the cost on the wrong side of a commission
     calculation, where nobody sees it again. */
  const cat = categoryOf(input.category)
  if (cat && cat.costType === null && !input.costType) {
    problems.push('Choose which side of the sale this cost belongs to.')
  }
  return problems
}

/**
 * Whether the commission on this vehicle has been calculated and locked, and when.
 *
 * Winnie, 11 Sep, section 3: the lock on a cost line is not a new permission system -
 * "lock behaviour follows the existing CostLineItem.is_locked rule from the commission
 * spec... don't build a second, separate edit-permission system for this screen." The
 * commission spec's own rule is section 9's: a locked commission is never recalculated,
 * and a later invoice goes through the P&L rather than through the commission. So the
 * thing that locks a cost line is the commission record on its vehicle, read here rather
 * than copied onto the expense as a second flag that could disagree with it.
 */
async function commissionLockedAt(dealId: string | null | undefined): Promise<string | null> {
  if (!dealId) return null
  try {
    const { data } = await supabase
      .from('commission_records')
      .select('locked_at')
      .eq('deal_id', dealId)
      .is('superseded_at', null)
      .is('voided_at', null)
      .not('locked_at', 'is', null)
      .limit(1)
    const row = (data || [])[0] as { locked_at?: string } | undefined
    return row?.locked_at || null
  } catch (e) {
    /* A lock that cannot be read is not a licence to edit, but refusing here would stop
       all correction whenever this table is briefly unreachable. The guarantee that
       matters - a locked commission is never recalculated - lives in the commission
       engine and holds either way, so this lets the edit through and says so. */
    console.error('Could not read the commission lock for this vehicle', e)
    return null
  }
}

const lockedMessage = (lockedAt: string) =>
  'Commission on this vehicle was calculated and locked on ' +
  String(lockedAt).slice(0, 10) +
  '. A locked commission is never recalculated, so this cost cannot be changed here. It ' +
  'has to be reopened first with a reason recorded - that flow is in the commission spec ' +
  'and is not built yet, so raise it rather than working around it.'

/** The fields worth reporting a change in. created_at and created_by are not edits. */
const AUDITED_FIELDS = [
  'deal_id', 'expense_type', 'category', 'cost_type', 'description', 'amount',
  'gst_amount', 'supplier', 'paid_to', 'invoice_ref', 'incurred_on', 'capitalised',
  'non_accounting', 'tax_invoice', 'bank_account', 'payment_method', 'reference',
  'po_number', 'bulk_payment_ref', 'payment_status', 'paid_at',
]
const NUMERIC_FIELDS = ['amount', 'gst_amount']
const DATE_FIELDS = ['paid_at']

/* PostgREST returns numerics as strings and timestamps in its own spelling, so a plain
   !== would report every save as having changed the amount and the payment date. */
const unchanged = (field: string, a: unknown, b: unknown): boolean => {
  if (a == null && b == null) return true
  if (NUMERIC_FIELDS.indexOf(field) >= 0) return Number(a || 0) === Number(b || 0)
  if (DATE_FIELDS.indexOf(field) >= 0) {
    const at = a ? Date.parse(String(a)) : NaN
    const bt = b ? Date.parse(String(b)) : NaN
    if (Number.isNaN(at) && Number.isNaN(bt)) return true
    return at === bt
  }
  return String(a ?? '') === String(b ?? '')
}

const changedFields = (before: Record<string, unknown>, after: Record<string, unknown>) => {
  const diff: Record<string, { from: unknown; to: unknown }> = {}
  for (const f of AUDITED_FIELDS) {
    const a = before[f] ?? null
    const b = after[f] ?? null
    if (!unchanged(f, a, b)) diff[f] = { from: a, to: b }
  }
  return diff
}

/**
 * Who changed what, when.
 *
 * Winnie, 11 Sep, section 3: "every edit logged (who, when, what changed) for audit,
 * consistent with the five-year record-keeping requirement already established for cost
 * and GST records." Never throws - an audit row that fails to write must not throw away
 * the correction it was recording.
 */
async function logCostChange(
  action: string,
  userId: string | null,
  expenseId: string,
  details: Record<string, unknown>,
): Promise<void> {
  try {
    await supabase.from('audit_log').insert([
      {
        actor_user_id: userId,
        action,
        target_name: expenseId,
        details,
        created_at: new Date().toISOString(),
      },
    ])
  } catch (e) {
    console.error('Could not write the audit entry for this cost (non-blocking)', e)
  }
}


export async function saveCost(input: CostInput, userId: string | null): Promise<void> {
  const problems = validateCost(input)
  if (problems.length) throw new Error(problems.join(' '))

  const record = toRecord(input, userId)

  if (input.id) {
    /* The row as it stands - both to say what changed, and to know which vehicle it is
       on. An edit can move a cost from one car to another, and it is the car it would be
       leaving whose locked commission decides whether that is allowed at all. */
    const { data: before } = await supabase
      .from('expenses')
      .select('*')
      .eq('id', input.id)
      .maybeSingle()

    const priorDealId = ((before as any) || {}).deal_id || null
    const lockedAt =
      (await commissionLockedAt(priorDealId)) ||
      (record.deal_id !== priorDealId ? await commissionLockedAt(record.deal_id) : null)
    if (lockedAt) throw new Error(lockedMessage(lockedAt))

    const patch = { ...record } as Record<string, unknown>
    delete patch.created_by

    /* A voided cost stays voided when it is corrected. toRecord writes payment_status
       from the form's paid tick, which knows nothing about void, so editing a voided row
       to fix a typo was quietly putting it back into the P&L. Found on 12 Sep by editing
       one and watching it come back to life. */
    if (((before as any) || {}).payment_status === 'void') {
      patch.payment_status = 'void'
      patch.paid_at = ((before as any) || {}).paid_at ?? null
    }
    const { error } = await supabase.from('expenses').update(patch).eq('id', input.id)
    if (error) throw error

    await logCostChange('expense_edited', userId, input.id, {
      dealId: record.deal_id,
      supplierInvoice: record.invoice_ref,
      changed: changedFields(((before as any) || {}) as Record<string, unknown>, patch),
    })
    return
  }

  const { data: created, error } = await supabase
    .from('expenses')
    .insert(record)
    .select('id')
    .single()
  if (error) throw error
  if (created?.id) {
    await logCostChange('expense_created', userId, created.id as string, {
      dealId: record.deal_id,
      amount: record.amount,
      supplierInvoice: record.invoice_ref,
      paidTo: record.paid_to,
    })
  }
}

/**
 * Her screen has a delete button. This voids instead.
 *
 * A cost that has been through the accounts is a record of money, and removing the row
 * would take it out of the P&L and the month-end stocktake with nothing left to show it
 * ever existed. Void keeps the row, drops it out of the totals, and can be undone.
 */
export async function voidCost(id: string, userId: string | null = null): Promise<void> {
  await guardAndSetStatus(id, 'void', 'expense_voided', userId, {})
}

export async function restoreCost(id: string, userId: string | null = null): Promise<void> {
  await guardAndSetStatus(id, 'awaiting_payment', 'expense_restored', userId, { paid_at: null })
}

/**
 * Voiding and restoring are edits too, so they answer to the same lock and leave the same
 * trail. Sharing one path is the point: a second way to change a cost is a second way to
 * get round the rule.
 */
async function guardAndSetStatus(
  id: string,
  status: string,
  action: string,
  userId: string | null,
  extra: Record<string, unknown>,
): Promise<void> {
  const { data: before } = await supabase
    .from('expenses')
    .select('deal_id, payment_status, amount, invoice_ref')
    .eq('id', id)
    .maybeSingle()

  const lockedAt = await commissionLockedAt(((before as any) || {}).deal_id || null)
  if (lockedAt) throw new Error(lockedMessage(lockedAt))

  const { error } = await supabase
    .from('expenses')
    .update({ payment_status: status, ...extra })
    .eq('id', id)
  if (error) throw error

  await logCostChange(action, userId, id, {
    dealId: ((before as any) || {}).deal_id || null,
    amount: ((before as any) || {}).amount ?? null,
    supplierInvoice: ((before as any) || {}).invoice_ref ?? null,
    changed: { payment_status: { from: ((before as any) || {}).payment_status ?? null, to: status } },
  })
}

const csvCell = (v: unknown) => {
  const s = v == null ? '' : String(v)
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
}

export function costOfSalesCsv(rows: CostRow[]): string {
  const head = ['Type', 'Date', 'Paid to', 'Supplier ABN', 'Stock', 'VIN', 'Vehicle', 'Category', 'Description',
    'Gross Amount', 'GST', 'Tax invoice', 'Non-accounting', 'Status', 'Date paid', 'Bank account',
    'Payment method', 'Reference', 'Supplier invoice', 'PO', 'Capitalised']
  const body = rows.map((r) => [
    typeLabel(r.expenseType), r.date || '', r.paidTo, formatAbn(r.supplierAbn), r.stockNumber, r.vin, r.vehicle,
    categoryLabel(r.category), r.description, r.amount.toFixed(2), r.gst.toFixed(2),
    r.taxInvoice ? 'Yes' : 'No', r.nonAccounting ? 'Yes' : 'No',
    r.status === 'billed' ? 'Billed' : r.status === 'paid' ? 'Paid' : 'Void',
    r.datePaid || '', r.bankAccount, r.paymentMethod, r.reference, r.supplierInvoice,
    r.poNumber, r.capitalised ? 'Yes' : 'No',
  ].map(csvCell).join(','))
  return [head.join(','), ...body].join('\r\n') + '\r\n'
}
